import Phaser from 'phaser';

import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { AvailableFontSizes, TextService } from '../../services/text-service';
import { Blackjack, GameEvent } from 'game-logic';
import { RoundInfoDividerViewModel } from './round-info-divider-view-model';
import { UxMetrics } from '../../model/ux-metrics';
import { BlackjackTranslationsType } from '../../model/blackjack-types';

export class RoundInfoDividerView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private graphics: Phaser.GameObjects.Graphics;
  private roundLabel: Phaser.GameObjects.Text;
  private streakLabel: Phaser.GameObjects.Text;
  private bonusLabel: Phaser.GameObjects.Text;
  private croupierLabel: Phaser.GameObjects.Text;
  private blackjackLabel: Phaser.GameObjects.Text;
  private fadeValue = 0;

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: RoundInfoDividerViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.graphics = scene.add.graphics();
    this.add(this.graphics);

    const mediumFontStyle = TextService.fontStyleNormal(
      AvailableFontSizes.Medium,
      this.viewModel.labelFill,
      this.viewModel.defaultFontFamily,
    );

    this.roundLabel = this.scene.add
      .text(
        0,
        0,
        `${viewModel.translatedText(BlackjackTranslationsType.Round)} 1/${
          Blackjack.LogicMetrics.TotalRounds
        }`,
        mediumFontStyle,
      )
      .setOrigin(0.0, 0.5);
    this.streakLabel = this.scene.add
      .text(
        0,
        0,
        `${viewModel.translatedText(BlackjackTranslationsType.Streak)} 0`,
        mediumFontStyle,
      )
      .setOrigin(0.5, 0.5);
    this.bonusLabel = this.scene.add
      .text(
        0,
        0,
        `${viewModel.translatedText(BlackjackTranslationsType.Bonus)} 0%`,
        mediumFontStyle,
      )
      .setOrigin(1.0, 0.5);
    this.add([this.roundLabel, this.streakLabel, this.bonusLabel]);

    this.croupierLabel = this.scene.add
      .text(
        0,
        0,
        viewModel.translatedText(BlackjackTranslationsType.DealerRules),
        TextService.fontStyleBold(
          AvailableFontSizes.Medium,
          this.viewModel.labelFill,
          this.viewModel.defaultFontFamily,
        ),
      )
      .setOrigin(0.5, 1.0);
    this.blackjackLabel = this.scene.add
      .text(
        0,
        0,
        viewModel.translatedText(BlackjackTranslationsType.BlackjackRules),
        TextService.fontStyleBold(
          AvailableFontSizes.Medium,
          this.viewModel.labelFill,
          this.viewModel.defaultFontFamily,
        ),
      )
      .setOrigin(0.5, 0.0);
    this.add([this.croupierLabel, this.blackjackLabel]);

    this.setName(RoundInfoDividerView.name);
    scene.add.existing(this);

    this.setupProgress(0);

    this.subscriptionGroup.add(
      this.viewModel.onResetGameScene$.subscribe(() => {
        this.roundLabel.text = `${viewModel.translatedText(BlackjackTranslationsType.Round)} 1/${
          Blackjack.LogicMetrics.TotalRounds
        }`;
        this.streakLabel.text = `${viewModel.translatedText(BlackjackTranslationsType.Streak)} 0`;
        this.bonusLabel.text = `${viewModel.translatedText(BlackjackTranslationsType.Bonus)} 0%`;

        this.fadeValue = 0;
        this.setupProgress(this.fadeValue);
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onGameSetupStart$.subscribe(() => {
        this.scene.tweens.add({
          targets: this,
          fadeValue: 1,
          duration: UxMetrics.LoadingFade,
          ease: Phaser.Math.Easing.Quadratic.InOut,
          onUpdate: (_tween) => {
            this.setupProgress(this.fadeValue);
          },
        });
      }),
    );
    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private setupProgress(progress: number): void {
    const primaryAlpha = progress / 0.7 + 0.3;
    this.roundLabel.setAlpha(primaryAlpha);
    this.streakLabel.setAlpha(primaryAlpha);
    this.bonusLabel.setAlpha(primaryAlpha);

    const secondaryAlpha = progress / 0.8 + 0.2;
    this.croupierLabel.setAlpha(1 - secondaryAlpha);
    this.blackjackLabel.setAlpha(1 - secondaryAlpha);
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    this.roundLabel.setFontSize(this.currentResizeMetrics.fontMetrics.Medium);
    this.streakLabel.setFontSize(this.currentResizeMetrics.fontMetrics.Medium);
    this.bonusLabel.setFontSize(this.currentResizeMetrics.fontMetrics.Medium);

    this.croupierLabel.setFontSize(this.currentResizeMetrics.fontMetrics.Medium);
    this.blackjackLabel.setFontSize(this.currentResizeMetrics.fontMetrics.Medium);

    const controlsWidth =
      (layoutConfig.boardWidth - layoutConfig.paddingSm * 2) * scalingMetrics.scaleFactor;
    const controlsHeight =
      (this.roundLabel.height + layoutConfig.paddingLg * 2) * scalingMetrics.scaleFactor;

    const xPos = layoutConfig.boardWidth * 0.5;
    const yPos = layoutConfig.boardHeight * 0.4;

    this.setSize(controlsWidth, controlsHeight);
    this.setPosition(
      xPos * scalingMetrics.scaleFactor + scalingMetrics.scaledOffsetX,
      yPos * scalingMetrics.scaleFactor,
    );

    this.roundLabel.setX(
      controlsWidth * -0.5 + layoutConfig.paddingLg * scalingMetrics.scaleFactor,
    );
    this.bonusLabel.setX(controlsWidth * 0.5 - layoutConfig.paddingLg * scalingMetrics.scaleFactor);

    const padding = layoutConfig.paddingLg * scalingMetrics.scaleFactor;
    this.croupierLabel.setY((controlsHeight + padding) * -0.5);
    this.blackjackLabel.setY((controlsHeight + padding) * 0.5);

    this.drawGraphics();
  }

  private drawGraphics(): void {
    this.graphics.clear(); // Clear previous drawings

    this.graphics.lineStyle(3, this.viewModel.borderColor, this.viewModel.borderAlpha);
    this.graphics.strokeRoundedRect(
      this.width * -0.5,
      this.height * -0.5,
      this.width,
      this.height,
      this.height * 0.5,
    );
    this.graphics.setDepth(1);
  }

  private async handleLogicEvents(event: GameEvent<Blackjack.BlackjackEvents>): Promise<void> {
    // Don't process instant events
    if (event.instant) {
      return;
    }

    switch (event.type) {
      case 'onCurrentRoundChanged':
        this.roundLabel.text = `${this.viewModel.translatedText(BlackjackTranslationsType.Round)} ${
          event.payload.value
        }/${Blackjack.LogicMetrics.TotalRounds}`;
        break;
      case 'onWinStreakChanged':
        {
          // TODO: animate streak emoji 🔥
          const streakCount = event.payload.value;
          this.streakLabel.text = `${this.viewModel.translatedText(
            BlackjackTranslationsType.Streak,
          )} ${streakCount}`;
          this.bonusLabel.text = `${this.viewModel.translatedText(
            BlackjackTranslationsType.Bonus,
          )} ${formatPercentage(Blackjack.calculateStreakMultiplier(streakCount))}`;
        }
        break;
      default:
        break;
    }
  }
}

function formatPercentage(value: number): string {
  return `${Math.round(value * 100)}%`;
}
